import React from 'react';

interface Step0TemplateSelectionProps {
  formData: {
    template: string;
  };
  updateFormData: (key: string, value: any) => void;
}

const Step0TemplateSelection: React.FC<Step0TemplateSelectionProps> = ({ formData, updateFormData }) => {
  return (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-lg font-medium text-gray-900 mb-2">How would you like to start?</h3>
        <p className="text-gray-600">Choose a pre-built template for common use cases or start from scratch</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Start from Scratch */}
        <label className="group cursor-pointer">
          <input 
            type="radio" 
            name="template" 
            value="blank" 
            className="sr-only peer" 
            checked={formData.template === 'blank'}
            onChange={(e) => updateFormData('template', e.target.value)}
          />
          <div className="relative bg-white border-2 border-gray-200 rounded-xl p-4 sm:p-6 peer-checked:border-netlify-teal peer-checked:bg-netlify-50 hover:border-gray-300 transition-all group-hover:shadow-lg">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Start from Scratch</h4>
                <p className="text-sm text-gray-600">Build your agent exactly how you want it</p>
              </div>
            </div>
            <div className="absolute top-4 right-4 w-6 h-6 border-2 border-gray-300 rounded-full peer-checked:border-netlify-teal peer-checked:bg-netlify-teal flex items-center justify-center">
              <svg className="w-4 h-4 text-white opacity-0 peer-checked:opacity-100" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
              </svg>
            </div>
          </div>
        </label>

        {/* Customer Support */}
        <label className="group cursor-pointer">
          <input 
            type="radio" 
            name="template" 
            value="customer_support" 
            className="sr-only peer" 
            checked={formData.template === 'customer_support'}
            onChange={(e) => updateFormData('template', e.target.value)}
          />
          <div className="relative bg-white border-2 border-gray-200 rounded-xl p-4 sm:p-6 peer-checked:border-blue-500 peer-checked:bg-blue-50 hover:border-gray-300 transition-all group-hover:shadow-lg">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Customer Support</h4>
                <p className="text-sm text-gray-600">Help customers with inquiries and issues</p>
              </div>
            </div>
            <div className="absolute top-4 right-4 w-6 h-6 border-2 border-gray-300 rounded-full peer-checked:border-blue-500 peer-checked:bg-blue-500 flex items-center justify-center">
              <svg className="w-4 h-4 text-white opacity-0 peer-checked:opacity-100" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
              </svg>
            </div>
          </div>
        </label>

        {/* Sales Assistant */}
        <label className="group cursor-pointer">
          <input 
            type="radio" 
            name="template" 
            value="sales_assistant" 
            className="sr-only peer" 
            checked={formData.template === 'sales_assistant'}
            onChange={(e) => updateFormData('template', e.target.value)}
          />
          <div className="relative bg-white border-2 border-gray-200 rounded-xl p-4 sm:p-6 peer-checked:border-green-500 peer-checked:bg-green-50 hover:border-gray-300 transition-all group-hover:shadow-lg">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200 transition-colors">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Sales Assistant</h4>
                <p className="text-sm text-gray-600">Generate leads and close deals</p>
              </div>
            </div>
            <div className="absolute top-4 right-4 w-6 h-6 border-2 border-gray-300 rounded-full peer-checked:border-green-500 peer-checked:bg-green-500 flex items-center justify-center">
              <svg className="w-4 h-4 text-white opacity-0 peer-checked:opacity-100" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
              </svg>
            </div>
          </div>
        </label>
      </div>
    </div>
  );
};

export default Step0TemplateSelection;
